// ---------------------------------------------------------------------------
// Persona Service (Unified)
// ---------------------------------------------------------------------------
// Unified API client for managing personas across Funnel, Discovery & Compare.
// Replaces separate persona methods in funnelService + userFiltersService.
// All personas now live in discovery_personas table via /api/v11/discovery/personas.
// ---------------------------------------------------------------------------

import { api } from '@/lib/api'
import type { DimensionCategory, UnitType } from '@/components/v11/funnel/types'

// ---------------------------------------------------------------------------
// Types (FE-facing — wire uses `type`, mapped at boundary)
// ---------------------------------------------------------------------------

export interface PersonaLayer {
  readonly category: DimensionCategory
  readonly dimension: string
  readonly values: readonly (string | number)[]
  readonly dimensionLabel: string
  readonly valueLabels: readonly string[]
}

export type PersonaType = 'funnel' | 'flat' | 'lens'

export interface Persona {
  readonly id: number
  readonly name: string
  readonly description: string | null
  readonly personaType: PersonaType
  readonly datasource: string
  readonly layers: readonly PersonaLayer[]
  readonly layerCount: number
  readonly flatFilters: Readonly<Record<string, string>>
  readonly finalCount: number
  readonly finalUnit: string
  readonly totalOrigin: number
  readonly isDefault: boolean
  readonly display: number
  readonly createdAt: string
  readonly updatedAt: string
}

export interface CreatePersonaInput {
  readonly name: string
  readonly description?: string
  readonly persona_type: PersonaType
  readonly layers: readonly {
    readonly category: DimensionCategory
    readonly dimension: string
    readonly values: readonly (string | number)[]
    readonly dimensionLabel?: string
    readonly valueLabels?: readonly string[]
  }[]
  readonly datasource?: string
  readonly finalCount?: number
  readonly finalUnit?: UnitType
  readonly totalOrigin?: number
  readonly is_default?: boolean
  readonly display?: number
}

export interface UpdatePersonaInput {
  readonly name?: string
  readonly description?: string
  readonly layers?: readonly {
    readonly category: DimensionCategory
    readonly dimension: string
    readonly values: readonly (string | number)[]
    readonly dimensionLabel?: string
    readonly valueLabels?: readonly string[]
  }[]
  readonly datasource?: string
  readonly finalCount?: number
  readonly finalUnit?: UnitType
  readonly totalOrigin?: number
  readonly is_default?: boolean
  readonly display?: number
}

// ---------------------------------------------------------------------------
// Wire layer (BE contract — field is `type`)
// ---------------------------------------------------------------------------

interface WirePersonaLayer {
  readonly type: DimensionCategory
  readonly dimension: string
  readonly values: readonly (string | number)[]
  readonly dimensionLabel: string
  readonly valueLabels: readonly string[]
}

interface WirePersona extends Omit<Persona, 'layers'> {
  readonly layers: readonly WirePersonaLayer[]
}

const personaLayerToWire = (l: PersonaLayer | CreatePersonaInput['layers'][number]) => ({
  type: l.category,
  dimension: l.dimension,
  values: l.values,
  dimensionLabel: l.dimensionLabel,
  valueLabels: l.valueLabels,
})

const personaLayerFromWire = (l: WirePersonaLayer): PersonaLayer => ({
  category: l.type,
  dimension: l.dimension,
  values: l.values,
  dimensionLabel: l.dimensionLabel,
  valueLabels: l.valueLabels,
})

const personaFromWire = (p: WirePersona): Persona => ({
  ...p,
  layers: p.layers.map(personaLayerFromWire),
})

const createInputToWire = (input: CreatePersonaInput) => ({
  ...input,
  layers: input.layers.map(personaLayerToWire),
})

const updateInputToWire = (input: UpdatePersonaInput) => ({
  ...input,
  layers: input.layers ? input.layers.map(personaLayerToWire) : undefined,
})

// ---------------------------------------------------------------------------
// Response Types (BE wire — `layers[].type`, mapped to `category` at boundary)
// ---------------------------------------------------------------------------

interface PersonaResponse {
  readonly success: boolean
  readonly data: WirePersona
}

interface PersonaListResponse {
  readonly success: boolean
  readonly data: readonly WirePersona[]
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const BASE_URL = '/api/v11/discovery/personas'

export const personaService = {
  /**
   * List all personas for the current user.
   * Optional type filter: 'funnel' or 'flat'.
   */
  async getAll(type?: PersonaType): Promise<readonly Persona[]> {
    const params = type ? { type } : undefined
    const response = await api.get<PersonaListResponse>(BASE_URL, { params })
    return (response as unknown as PersonaListResponse).data.map(personaFromWire)
  },

  /**
   * Get a specific persona by ID.
   */
  async getById(id: number): Promise<Persona> {
    const response = await api.get<PersonaResponse>(`${BASE_URL}/${id}`)
    return personaFromWire((response as unknown as PersonaResponse).data)
  },

  /**
   * Get the user's default persona.
   */
  async getDefault(): Promise<Persona | null> {
    try {
      const response = await api.get<PersonaResponse>(`${BASE_URL}/default`)
      return personaFromWire((response as unknown as PersonaResponse).data)
    } catch (error: unknown) {
      const err = error as { response?: { status?: number } }
      if (err?.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Create a new persona.
   */
  async create(input: CreatePersonaInput): Promise<Persona> {
    const response = await api.post<PersonaResponse>(BASE_URL, createInputToWire(input))
    return personaFromWire((response as unknown as PersonaResponse).data)
  },

  /**
   * Update an existing persona.
   */
  async update(id: number, input: UpdatePersonaInput): Promise<Persona> {
    const response = await api.put<PersonaResponse>(`${BASE_URL}/${id}`, updateInputToWire(input))
    return personaFromWire((response as unknown as PersonaResponse).data)
  },

  /**
   * Delete a persona by ID.
   */
  async delete(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/${id}`)
  },

  /**
   * Set a persona as the user's default.
   */
  async setDefault(id: number): Promise<Persona> {
    const response = await api.post<PersonaResponse>(
      `${BASE_URL}/${id}/set-default`
    )
    return personaFromWire((response as unknown as PersonaResponse).data)
  },
}
